import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import {
  copyHistoryStateWithoutOverlayFlag,
  ignoreNextModalHistoryPops,
  whenModalHistoryIdle,
} from "../../utils/modalHistoryBack";
import {
  OPEN_OWN_PROFILE_AFTER_EDIT_SAVE_STORAGE_KEY,
  parseProfileHref,
  profileModalBackgroundState,
  type CloseProfileModalDetail,
} from "../../utils/profileModalNavigation";
import { useUser } from "../../Data/UserContext";
import { tryTrackProfileLinkJoinedFromModalOpen } from "../../utils/profileLinkTracking";
import AuthenticatedRoute from "../AuthenticatedRoute";
import ProfilePage from "./index";
import PublicProfile from "./PublicProfile";
import { ProfileModalShell } from "./ProfileModalShell";

function profileModalTargetKey(t: ProfileGlobalModalTarget): string {
  if (t.kind === "own") return "own";
  return `pub-${t.p ?? ""}-${t.u ?? ""}`;
}

export type ProfileGlobalModalTarget =
  | { kind: "own" }
  | { kind: "public"; p?: string; u?: string };

type ProfileGlobalModalContextValue = {
  openOwnProfile: (opts?: { openEdit?: boolean }) => void;
  openPublicProfile: (opts: { p?: string; u?: string }) => void;
  openProfileFromHref: (href: string) => void;
  closeProfileModal: () => void;
  /** Clear the overlay without `history.back()` — use before navigating to a non-profile route. */
  dismissProfileModalForNavigation: () => void;
};

const ProfileGlobalModalContext = createContext<ProfileGlobalModalContextValue | null>(null);

export function useProfileGlobalModal(): ProfileGlobalModalContextValue {
  const ctx = useContext(ProfileGlobalModalContext);
  if (!ctx) {
    throw new Error("useProfileGlobalModal must be used within ProfileGlobalModalProvider");
  }
  return ctx;
}

/**
 * Open a public profile after the current overlay unmounts *and* its dummy
 * `history.back()` has settled. Call, then close the overlay.
 *
 * Opening in the same turn as overlay release is popped by that back navigation.
 * Opening in `useEffect` after unmount is still too early — the popstate is async.
 */
export function useOpenPublicProfileAfterOverlayClose(
  overlayOpen: boolean
): (opts: { p?: string; u?: string }) => void {
  const { openPublicProfile } = useProfileGlobalModal();
  const pendingRef = useRef<{ p?: string; u?: string } | null>(null);

  useEffect(() => {
    if (overlayOpen) return;
    const pending = pendingRef.current;
    if (!pending) return;
    return whenModalHistoryIdle(() => {
      if (pendingRef.current !== pending) return;
      pendingRef.current = null;
      openPublicProfile(pending);
    });
  }, [overlayOpen, openPublicProfile]);

  return useCallback((opts: { p?: string; u?: string }) => {
    pendingRef.current = {
      p: opts.p?.trim() || undefined,
      u: opts.u?.trim() || undefined,
    };
  }, []);
}

function ProfileGlobalModalLayer({
  target,
  onClose,
}: {
  target: ProfileGlobalModalTarget | null;
  onClose: () => void;
}) {
  const { user } = useUser();

  const identifier =
    target && target.kind === "public" ? (target.p || target.u || "").trim() : "";

  const isOwnPublicLink =
    Boolean(target) &&
    target!.kind === "public" &&
    Boolean(user && identifier) &&
    (user!.playerId === identifier ||
      String(user!.userId) === identifier ||
      Boolean(user!.username && user!.username === identifier));

  let inner: React.ReactNode = null;
  if (target) {
    if (target.kind === "own" || (target.kind === "public" && isOwnPublicLink)) {
      // Skip AuthenticatedRoute when session is already ready — its loading shell
      // flashes an empty frame inside the modal on every open.
      const page = <ProfilePage onClose={onClose} embedInGlobalModal />;
      inner = user ? page : <AuthenticatedRoute>{page}</AuthenticatedRoute>;
    } else if (target.kind === "public" && identifier) {
      inner = (
        <PublicProfile
          identifier={identifier}
          onClose={onClose}
        />
      );
    }
  }

  const layer = (
    <AnimatePresence>
      {target && inner ? (
        <ProfileModalShell key={profileModalTargetKey(target)} onClose={onClose}>
          {inner}
        </ProfileModalShell>
      ) : null}
    </AnimatePresence>
  );

  if (typeof document === "undefined") return layer;
  return createPortal(layer, document.body);
}

export function ProfileGlobalModalProvider({ children }: { children: React.ReactNode }) {
  const [targetStack, setTargetStack] = useState<ProfileGlobalModalTarget[]>([]);
  const target = targetStack.length > 0 ? targetStack[targetStack.length - 1] : null;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();

  /**
   * Pop dummy profile history entries. Only goes back while the *current* entry is
   * still a profile-modal push — otherwise `history.go(-N)` would leave the page.
   */
  const popProfileModalHistory = useCallback((depth: number, absorbOverlayPops: boolean) => {
    if (typeof window === "undefined" || depth <= 0 || !window.history?.go) return;
    const state = window.history.state as {
      __profileGlobalModal?: boolean;
      __profileModalDepth?: unknown;
    } | null;
    if (!state?.__profileGlobalModal) return;
    const stateDepth =
      typeof state.__profileModalDepth === "number" && state.__profileModalDepth > 0
        ? Math.trunc(state.__profileModalDepth)
        : 1;
    const goBy = Math.min(depth, stateDepth);
    if (goBy <= 0) return;
    // Only when opening another overlay after close — swallow these pops so the
    // next overlay's history entry is not dismissed by this go().
    if (absorbOverlayPops) {
      ignoreNextModalHistoryPops(goBy);
    }
    window.history.go(-goBy);
  }, []);

  const closeProfileModal = useCallback(() => {
    const depth = targetStack.length;
    setTargetStack([]);
    popProfileModalHistory(depth, false);
  }, [targetStack.length, popProfileModalHistory]);

  const dismissProfileModalForNavigation = useCallback(() => {
    setTargetStack([]);
  }, []);

  const pushModalTarget = useCallback((nextTarget: ProfileGlobalModalTarget) => {
    // Keep pushState out of the setState updater — React may invoke updaters twice
    // in Strict Mode, which would stack duplicate history entries and make close
    // walk back past the current page.
    let nextDepth = 0;
    setTargetStack((prev) => {
      const next = [...prev, nextTarget];
      nextDepth = next.length;
      return next;
    });
    if (typeof window !== "undefined" && window.history?.pushState && nextDepth > 0) {
      window.history.pushState(
        {
          ...copyHistoryStateWithoutOverlayFlag(window.history.state),
          __profileGlobalModal: true,
          __profileModalDepth: nextDepth,
        },
        ""
      );
    }
  }, []);

  useEffect(() => {
    const onClose = (ev: Event) => {
      const afterClose = (ev as CustomEvent<CloseProfileModalDetail>).detail?.afterClose;
      const depth = targetStack.length;
      const openingNextOverlay = typeof afterClose === "function";
      setTargetStack([]);
      popProfileModalHistory(depth, openingNextOverlay);
      if (!openingNextOverlay) return;
      // `/profile` route also listens; let it invoke afterClose after it navigates away.
      if (depth <= 0) {
        if (typeof window !== "undefined" && window.location.pathname === "/profile") {
          return;
        }
        afterClose();
        return;
      }
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        window.removeEventListener("popstate", onPop);
        window.clearTimeout(fallbackId);
        // Defer so peer popstate handlers finish before the next overlay pushState.
        window.setTimeout(afterClose, 0);
      };
      const onPop = () => finish();
      window.addEventListener("popstate", onPop);
      const fallbackId = window.setTimeout(finish, 150);
    };
    window.addEventListener("closeProfileModal", onClose);
    return () => window.removeEventListener("closeProfileModal", onClose);
  }, [popProfileModalHistory, targetStack.length]);

  useEffect(() => {
    if (location.pathname === "/edit-profile" || location.pathname === "/xp-leaderboard") {
      setTargetStack([]);
    }
  }, [location.pathname]);

  useEffect(() => {
    const onPopState = (ev: PopStateEvent) => {
      setTargetStack((prev) => {
        if (prev.length === 0) return prev;
        const rawDepth = (ev.state as { __profileModalDepth?: unknown } | null)?.__profileModalDepth;
        const depthFromState = typeof rawDepth === "number" ? Math.trunc(rawDepth) : null;
        const nextDepth =
          depthFromState != null
            ? Math.max(0, Math.min(prev.length, depthFromState))
            : Math.max(0, prev.length - 1);
        return prev.slice(0, nextDepth);
      });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const openOwnProfile = useCallback(
    (opts?: { openEdit?: boolean }) => {
      if (opts?.openEdit) {
        setTargetStack([]);
        navigate("/edit-profile", { state: profileModalBackgroundState(location) });
        return;
      }
      pushModalTarget({ kind: "own" });
    },
    [navigate, location, pushModalTarget]
  );

  useEffect(() => {
    if (location.pathname === "/edit-profile") {
      return;
    }
    let open = false;
    try {
      open = sessionStorage.getItem(OPEN_OWN_PROFILE_AFTER_EDIT_SAVE_STORAGE_KEY) === "1";
      if (open) {
        sessionStorage.removeItem(OPEN_OWN_PROFILE_AFTER_EDIT_SAVE_STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
    if (!open) {
      return;
    }
    if (location.pathname === "/profile") {
      return;
    }
    openOwnProfile();
  }, [location.pathname, openOwnProfile]);

  const openPublicProfile = useCallback((opts: { p?: string; u?: string }) => {
    pushModalTarget({
      kind: "public",
      p: opts.p?.trim() || undefined,
      u: opts.u?.trim() || undefined,
    });
    tryTrackProfileLinkJoinedFromModalOpen({
      p: opts.p,
      u: opts.u,
      viewer: user,
      user_id: user?.userId,
      pathname: location.pathname,
    });
  }, [pushModalTarget, user, location.pathname]);

  const openProfileFromHref = useCallback(
    (href: string) => {
      const parsed = parseProfileHref(href);
      if (!parsed) return;
      if (parsed.type === "own") {
        if (parsed.openEdit) {
          setTargetStack([]);
          navigate("/edit-profile", { state: profileModalBackgroundState(location) });
        } else {
          pushModalTarget({ kind: "own" });
        }
      } else {
        pushModalTarget({
          kind: "public",
          p: parsed.p,
          u: parsed.u,
        });
        tryTrackProfileLinkJoinedFromModalOpen({
          p: parsed.p,
          u: parsed.u,
          viewer: user,
          user_id: user?.userId,
          pathname: location.pathname,
        });
      }
    },
    [navigate, location, pushModalTarget, user]
  );

  const value = useMemo(
    () => ({
      openOwnProfile,
      openPublicProfile,
      openProfileFromHref,
      closeProfileModal,
      dismissProfileModalForNavigation,
    }),
    [openOwnProfile, openPublicProfile, openProfileFromHref, closeProfileModal, dismissProfileModalForNavigation]
  );

  return (
    <ProfileGlobalModalContext.Provider value={value}>
      {children}
      <ProfileGlobalModalLayer target={target} onClose={closeProfileModal} />
    </ProfileGlobalModalContext.Provider>
  );
}
